import React, { useEffect, useState } from "react";
import { theme, message, Grid, Avatar, Progress } from "antd";
import {
  UserOutlined, ShoppingOutlined, QrcodeOutlined, TrophyOutlined,
  ArrowRightOutlined, FireFilled, ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getBackendUrl } from "../../utils/env";
import axios from "axios";
import moment from "moment";
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer,
} from "recharts";

const { useBreakpoint } = Grid;

/* ─── ESTILOS DINÁMICOS ───────────────────────────────────────────────────── */
const makeStyles = (t) => `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Mulish:wght@400;500;600&display=swap');

  .pf-root {
    font-family: 'Mulish', sans-serif;
    background: ${t.colorBgLayout};
    min-height: 100vh;
    padding: clamp(20px, 5vw, 44px) clamp(16px, 4vw, 40px) 80px;
    max-width: 900px;
    margin: 0 auto;
  }

  /* ── Sección genérica ── */
  .pf-section { margin-bottom: 24px; }
  .pf-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: ${t.colorTextTertiary};
    margin-bottom: 14px;
  }

  /* ── Hero card ── */
  .pf-hero {
    background: ${t.colorBgContainer};
    border: 1px solid ${t.colorBorderSecondary};
    border-radius: 24px;
    padding: clamp(24px, 5vw, 40px);
    display: flex;
    gap: 28px;
    align-items: flex-start;
    flex-wrap: wrap;
    margin-bottom: 24px;
    position: relative;
    overflow: hidden;
    animation: fadeUp 0.45s ease both;
  }
  .pf-hero-accent {
    position: absolute;
    width: 260px;
    height: 260px;
    border-radius: 50%;
    background: #ffe000;
    opacity: 0.06;
    top: -80px;
    right: -60px;
    filter: blur(50px);
    pointer-events: none;
  }
  .pf-hero-avatar-wrap {
    position: relative;
    flex-shrink: 0;
  }
  .pf-hero-avatar-ring {
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    border: 3px solid ${t.colorWarning};
    opacity: 0.5;
    animation: ringPulse 2.4s ease-in-out infinite;
  }
  @keyframes ringPulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50%       { transform: scale(1.07); opacity: 0.15; }
  }
  .pf-hero-info { flex: 1; min-width: 0; }
  .pf-hero-name {
    font-family: 'Syne', sans-serif;
    font-size: clamp(24px, 6vw, 36px);
    font-weight: 800;
    color: ${t.colorText};
    line-height: 1.1;
    margin: 0 0 4px;
  }
  .pf-hero-sub {
    font-size: 13px;
    color: ${t.colorTextSecondary};
    margin-bottom: 20px;
  }
  .pf-hero-sub strong { color:  #ffe000; font-weight: 700; }

  /* Bravos barra */
  .pf-points-box {
    background: ${t.colorFillAlter};
    border: 1px solid ${t.colorBorderSecondary};
    border-radius: 16px;
    padding: 18px 20px;
  }
  .pf-points-top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 10px;
    gap: 8px;
    flex-wrap: wrap;
  }
  .pf-points-val {
    font-family: 'Syne', sans-serif;
    font-size: clamp(28px, 7vw, 40px);
    font-weight: 800;
    color:  #ffe000;
    line-height: 1;
  }
  .pf-points-label {
    font-size: 11px;
    color: ${t.colorTextTertiary};
    letter-spacing: 2px;
    text-transform: uppercase;
    align-self: center;
  }
  .pf-points-goal {
    font-size: 12px;
    color: ${t.colorTextSecondary};
    align-self: flex-end;
  }
  .pf-expiry {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    font-size: 12px;
    color: ${t.colorError};
  }

  /* ── 2-col grid ── */
  .pf-grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }
  @media (max-width: 600px) { .pf-grid-2 { grid-template-columns: 1fr; } }

  /* ── Chart card ── */
  .pf-card {
    background: ${t.colorBgContainer};
    border: 1px solid ${t.colorBorderSecondary};
    border-radius: 20px;
    overflow: hidden;
    animation: fadeUp 0.4s ease both;
  }
  .pf-card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    border-bottom: 1px solid ${t.colorBorderSecondary};
  }
  .pf-card-head-title {
    font-family: 'Syne', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: ${t.colorText};
    letter-spacing: 0.3px;
  }
  .pf-card-body { padding: 20px; }
  .pf-card-body--flush { padding: 0; }

  /* ── Acciones rápidas ── */
  .pf-actions { display: flex; flex-direction: column; gap: 10px; }
  .pf-action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    background: ${t.colorBgContainer};
    border: 1px solid ${t.colorBorderSecondary};
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.18s;
    text-decoration: none;
    animation: fadeUp 0.4s ease both;
  }
  .pf-action:hover {
    border-color: ${t.colorWarning}70;
    background: ${t.colorPrimaryBg};
    transform: translateX(4px);
  }
  .pf-action-left { display: flex; align-items: center; gap: 14px; }
  .pf-action-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  }
  .pf-action-title {
    font-size: 14px;
    font-weight: 600;
    color: ${t.colorText};
  }
  .pf-action-arrow { color: ${t.colorTextTertiary}; font-size: 12px; }

  /* ── Historial ── */
  .pf-history-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 13px 20px;
    border-bottom: 1px solid ${t.colorBorderSecondary};
    transition: background 0.15s;
    animation: fadeUp 0.3s ease both;
  }
  .pf-history-row:last-child { border-bottom: none; }
  .pf-history-row:hover { background: ${t.colorFillAlter}; }
  .pf-history-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ffe000;
    flex-shrink: 0;
    opacity: 0.6;
  }
  .pf-history-desc {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
    color: ${t.colorText};
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .pf-history-date {
    font-size: 11px;
    color: ${t.colorTextTertiary};
    flex-shrink: 0;
  }
  .pf-history-pts {
    font-family: 'Syne', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #ffe000;
    flex-shrink: 0;
    white-space: nowrap;
  }

  /* ── Info personal ── */
  .pf-info-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
  @media (max-width: 640px) { .pf-info-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 400px) { .pf-info-grid { grid-template-columns: 1fr; } }
  .pf-info-item {
    background: ${t.colorFillAlter};
    border: 1px solid ${t.colorBorderSecondary};
    border-radius: 14px;
    padding: 14px 16px;
  }
  .pf-info-item-label {
    font-size: 10px;
    color: ${t.colorTextTertiary};
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  .pf-info-item-val {
    font-size: 14px;
    font-weight: 600;
    color: ${t.colorText};
    word-break: break-word;
  }
  .pf-sacramento-tag {
    display: inline-block;
    background: ${t.colorWarningBg};
    color: #ffe000;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
    margin: 2px;
    border: 1px solid ${t.colorWarningBorder};
  }

  /* ── Skeleton ── */
  @keyframes shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position:  600px 0; }
  }
  .skeleton {
    border-radius: 8px;
    background: linear-gradient(90deg, ${t.colorFillAlter} 25%, ${t.colorFillSecondary} 50%, ${t.colorFillAlter} 75%);
    background-size: 600px;
    animation: shimmer 1.4s infinite linear;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

/* ─── PERFIL ──────────────────────────────────────────────────────────────── */
export default function Perfil() {
  const navigate      = useNavigate();
  const { token }     = theme.useToken();
  const screens       = useBreakpoint();
  const css           = makeStyles(token);

  const bravoColor = "#ffe000";

  const [user, setUser]           = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [histPage, setHistPage]   = useState(0);
  const PAGE_SIZE = 5;

  /* fetch */
  useEffect(() => {
    (async () => {
      try {
        const userId = localStorage.getItem("user_id") || sessionStorage.getItem("user_id");
        const tkn    = localStorage.getItem("token")   || sessionStorage.getItem("token");
        if (!userId || !tkn) return navigate("/");

        const res = await axios.get(
          `${getBackendUrl()}api/user.php?id=${userId}&fallback_token=${tkn}`,
          { headers: { Authorization: `Bearer ${tkn}` }, timeout: 8000 }
        );

        let data = res.data;
        if (typeof data === "string") data = JSON.parse(data);

        if (data.success) {
          setUser(data.user);
          setActivities(
            (data.actividades || []).map((a, i) => ({
              id: i, description: a.actividad, points: a.points, date: a.timestamp,
            }))
          );
        } else {
          message.error(data.message || "Error al obtener el perfil");
        }
      } catch (e) {
        console.error(e);
        message.error("Error de red o timeout");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* chart data */
  const chartData = (() => {
    const grouped = {};
    activities.forEach((a) => {
      const d = moment(a.date).format("DD/MM");
      grouped[d] = (grouped[d] || 0) + a.points;
    });
    return Object.entries(grouped).map(([date, bravos]) => ({ date, bravos }));
  })();

  const progressPct = Math.min(((user?.points || 0) / 10000) * 100, 100);

  const acciones = [
    { icon: <ShoppingOutlined />, title: "Tienda",   path: "/store",   bg: `${token.colorError}18`,   color: token.colorError   },
    { icon: <QrcodeOutlined />,  title: "Escanear",  path: "/scan",    bg: `${token.colorSuccess}18`, color: token.colorSuccess },
    { icon: <TrophyOutlined />,  title: "Ranking",   path: "/ranking", bg: `${token.colorPrimary}18`, color: token.colorPrimary },
  ];

  const infoFields = user ? [
    { label: "Correo",    value: user.email || "N/A" },
    { label: "Sexo",      value: user.gender || "N/A" },
    { label: "Edad",      value: user.birth_date ? `${moment().diff(moment(user.birth_date), "years")} años` : "N/A" },
    { label: "Miembro desde", value: user.created_at ? moment(user.created_at).format("MMM YYYY") : "N/A" },
    {
      label: "Sacramentos",
      value: user.sacraments
        ? user.sacraments.split(",").map((s) => (
            <span key={s} className="pf-sacramento-tag">{s.trim()}</span>
          ))
        : "Ninguno",
      wide: true,
    },
  ] : [];

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <div className="pf-root" style={{ fontFamily: "'Mulish', sans-serif", background: token.colorBgLayout }}>
        <style>{css}</style>
        <div className="pf-hero" style={{ gap: 20 }}>
          <div className="skeleton" style={{ width: 96, height: 96, borderRadius: "50%", flexShrink: 0 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="skeleton" style={{ width: "55%", height: 28 }} />
            <div className="skeleton" style={{ width: "35%", height: 14 }} />
            <div className="skeleton" style={{ height: 72, borderRadius: 14, marginTop: 8 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!user?.id) return null;

  /* ── Vista ── */
  return (
    <div className="pf-root">
      <style>{css}</style>

      {/* ── HERO ── */}
      <div className="pf-hero pf-section">
        <div className="pf-hero-accent" />

        <div className="pf-hero-avatar-wrap">
          <div className="pf-hero-avatar-ring" />
          <Avatar
            size={screens.xs ? 80 : 100}
            src={user.profile_picture ? `${getBackendUrl()}${user.profile_picture}` : null}
            icon={<UserOutlined />}
            style={{
              border: `3px solid ${bravoColor}`,
              backgroundColor: token.colorFillAlter,
              display: "block",
              position: "relative",
              zIndex: 1,
            }}
          />
        </div>

        <div className="pf-hero-info">
          <h1 className="pf-hero-name">{user.full_name}</h1>
          <p className="pf-hero-sub">
            Dinámica:{" "}
            <strong>{user.dinamica ? `${user.dinamica} · ¡La mejor!` : "N/A"}</strong>
          </p>

          <div className="pf-points-box">
            <div className="pf-points-top">
              <div>
                <div className="pf-points-label">Bravos acumulados</div>
                <div className="pf-points-val">
                  {(user.points || 0).toLocaleString()}
                  <span style={{ fontSize: "0.45em", marginLeft: 6, opacity: 0.7 }}>B</span>
                </div>
              </div>
              <div className="pf-points-goal">Meta: 10 000 B</div>
            </div>
            <Progress
              percent={progressPct}
              strokeColor={bravoColor}
              railColor={token.colorBorderSecondary}
              strokeWidth={10}
              showInfo={false}
            />
            <div className="pf-expiry">
              <ClockCircleOutlined style={{ fontSize: 12 }} />
              Tus Bravos caducan el 1 de enero · ¡Úsalos pronto!
            </div>
          </div>
        </div>
      </div>

      {/* ── GRÁFICA + ACCIONES ── */}
      <div className="pf-grid-2 pf-section">
        {/* Gráfica */}
        <div className="pf-card" style={{ animationDelay: "0.05s" }}>
          <div className="pf-card-head">
            <FireFilled style={{ color: bravoColor, fontSize: 14 }} />
            <span className="pf-card-head-title">Gráfica de Bravos</span>
          </div>
          <div className="pf-card-body">
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={token.colorBorderSecondary} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: token.colorTextTertiary }} axisLine={false} tickLine={false} />
                <YAxis hide domain={[0, "auto"]} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: token.colorBgElevated,
                    borderRadius: 10,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    boxShadow: token.boxShadowSecondary,
                    fontSize: 12,
                    color: token.colorText,
                  }}
                  itemStyle={{ color: bravoColor, fontWeight: 600 }}
                />
                <Line
                  type="monotone"
                  dataKey="bravos"
                  stroke={bravoColor}
                  strokeWidth={3}
                  dot={{ fill: token.colorPrimary, r: 3 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Acciones rápidas */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="pf-label">Ir a</div>
          <div className="pf-actions">
            {acciones.map((a, i) => (
              <div
                key={i}
                className="pf-action"
                style={{ animationDelay: `${0.08 + i * 0.06}s` }}
                onClick={() => navigate(a.path)}
              >
                <div className="pf-action-left">
                  <div className="pf-action-icon" style={{ background: a.bg, color: a.color }}>
                    {a.icon}
                  </div>
                  <span className="pf-action-title">{a.title}</span>
                </div>
                <ArrowRightOutlined className="pf-action-arrow" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── HISTORIAL ── */}
      <div className="pf-section">
        <div className="pf-label">Historial de actividades</div>
        <div className="pf-card">
          <div className="pf-card-head">
            <ClockCircleOutlined style={{ color: bravoColor, fontSize: 14 }} />
            <span className="pf-card-head-title">Últimos registros</span>
            <span style={{ marginLeft: "auto", fontSize: 12, color: token.colorTextTertiary }}>
              {activities.length} actividades
            </span>
          </div>
          <div className="pf-card-body--flush">
            {activities.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center", color: token.colorTextTertiary, fontSize: 13 }}>
                Sin actividades registradas aún.
              </div>
            ) : (
              <>
                {activities
                  .slice(histPage * PAGE_SIZE, (histPage + 1) * PAGE_SIZE)
                  .map((a, i) => (
                    <div key={a.id} className="pf-history-row" style={{ animationDelay: `${i * 0.04}s` }}>
                      <div className="pf-history-dot" />
                      <span className="pf-history-desc">{a.description}</span>
                      <span className="pf-history-date">{moment(a.date).format("DD MMM")}</span>
                      <span className="pf-history-pts">+{a.points} B</span>
                    </div>
                  ))}
                {/* Paginación simple */}
                {activities.length > PAGE_SIZE && (
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 20px", borderTop: `1px solid ${token.colorBorderSecondary}`
                  }}>
                    <button
                      onClick={() => setHistPage((p) => Math.max(0, p - 1))}
                      disabled={histPage === 0}
                      style={{
                        background: "none", border: "none", cursor: histPage === 0 ? "default" : "pointer",
                        color: histPage === 0 ? token.colorTextDisabled : bravoColor,
                        fontFamily: "'Mulish', sans-serif", fontWeight: 600, fontSize: 13,
                      }}
                    >← Anterior</button>
                    <span style={{ fontSize: 12, color: token.colorTextTertiary }}>
                      {histPage + 1} / {Math.ceil(activities.length / PAGE_SIZE)}
                    </span>
                    <button
                      onClick={() => setHistPage((p) => Math.min(Math.ceil(activities.length / PAGE_SIZE) - 1, p + 1))}
                      disabled={(histPage + 1) * PAGE_SIZE >= activities.length}
                      style={{
                        background: "none", border: "none",
                        cursor: (histPage + 1) * PAGE_SIZE >= activities.length ? "default" : "pointer",
                        color: (histPage + 1) * PAGE_SIZE >= activities.length ? token.colorTextDisabled : bravoColor,
                        fontFamily: "'Mulish', sans-serif", fontWeight: 600, fontSize: 13,
                      }}
                    >Siguiente →</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── INFO PERSONAL ── */}
      <div className="pf-section">
        <div className="pf-label">Información personal</div>
        <div className="pf-info-grid">
          {infoFields.map((f, i) => (
            <div
              key={i}
              className="pf-info-item"
              style={f.wide ? { gridColumn: "1 / -1" } : {}}
            >
              <div className="pf-info-item-label">{f.label}</div>
              <div className="pf-info-item-val">{f.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
